import Pubnub from 'pubnub';
import { ON_URL_PREVIEW_RECEIVED_EVENT_TYPE } from 'core/constants';
import type { KustomerEventTypes } from 'core_main/types';
import { runListenersForEvent, type RunListenersForEventCallbackResponse } from 'core_main/globals/events';
import { getStore } from 'core_main/store';
import { parseUrlUnfurled } from '../helpers/parseUrlUnfurled';
import type { UrlPreviewCallbackResponse } from '../types';
import { UrlUnfurled } from '../types/urlUnfurled';

/**
 * Dependencies interface for handleUrlUnfurled function
 * Supports dependency injection for testing and customization
 */
interface HandleUrlUnfurledDependencies {
  /** Function to get the current store state */
  getStore: () => { enableUrlUnfurling: boolean };
  /** Function to parse URL unfurled data from PubNub message */
  parseUrlUnfurled: (message: Pubnub.MessageEvent['message']) => UrlUnfurled | null;
  /** Function to emit events to core listeners */
  emitEvent: (event: KustomerEventTypes, data?: RunListenersForEventCallbackResponse | null) => void;
  /** Function to log errors during processing */
  logError: (message: string, error?: unknown) => void;
}

/**
 * Default dependencies for handleUrlUnfurled function
 * Uses real implementations for all dependencies
 */
const defaultHandleUrlUnfurledDeps: HandleUrlUnfurledDependencies = {
  getStore,
  parseUrlUnfurled,
  emitEvent: (event, data) => runListenersForEvent(event, data),
  logError: (message, error) => console.error(message, error),
};

/**
 * Handle URL unfurled event from PubNub
 *
 * Processes URL unfurled events from both inbound and outbound messages.
 * This handler is called for both:
 * - `kustomer.app.chat.url.unfurled.send` (outbound agent messages)
 * - `kustomer.app.chat.url.unfurled.receive` (inbound user messages)
 *
 * CRITICAL: This is a preview message and does NOT:
 * - Increment unread counts
 * - Mark messages as delivered
 * - Track read status
 * - Update conversation preview
 * - Trigger notifications
 *
 * The function parses the URL unfurled data and emits an event to the widget layer,
 * which handles updating message state and UI. The event signature matches iOS and Android
 * implementations for consistency across platforms.
 *
 * @param messageEvent - PubNub message event containing URL unfurled event
 * @param deps - Optional dependencies object for dependency injection (defaults to real implementations)
 *
 * @example
 * ```typescript
 * // Called automatically by PubNub listener
 * handleUrlUnfurled(pubnubMessageEvent);
 *
 * // With custom dependencies for testing
 * handleUrlUnfurled(pubnubMessageEvent, {
 *   parseUrlUnfurled: mockParseFunction,
 *   emitEvent: mockEmitFunction,
 * });
 * ```
 */
export function handleUrlUnfurled(
  messageEvent: Pubnub.MessageEvent,
  deps: Partial<HandleUrlUnfurledDependencies> = {},
): void {
  const {
    getStore: getStoreFn = defaultHandleUrlUnfurledDeps.getStore,
    parseUrlUnfurled: parseFn = defaultHandleUrlUnfurledDeps.parseUrlUnfurled,
    emitEvent = defaultHandleUrlUnfurledDeps.emitEvent,
    logError = defaultHandleUrlUnfurledDeps.logError,
  } = deps;

  // Early return if URL unfurling is disabled
  const { enableUrlUnfurling } = getStoreFn();
  if (!enableUrlUnfurling) {
    return;
  }

  const urlUnfurled = parseFn(messageEvent.message);

  if (!urlUnfurled) {
    logError('Failed to parse URL unfurled from message', messageEvent);
    return;
  }

  const { conversationId, originalMessageId } = urlUnfurled;

  if (!conversationId || !originalMessageId) {
    logError('Missing conversationId or originalMessageId for URL unfurled', urlUnfurled);
    return;
  }

  // CRITICAL: This is a preview message - do NOT:
  // - Increment unread counts
  // - Mark as delivered
  // - Track read status
  // - Update conversation preview
  // - Trigger notifications

  // Emit event to widget - widget handler will update message state
  // CRITICAL: Signature must match iOS and Android implementations
  const payload: UrlPreviewCallbackResponse = {
    conversationId,
    messageId: originalMessageId,
    urlUnfurled,
  };
  emitEvent(ON_URL_PREVIEW_RECEIVED_EVENT_TYPE, payload);
}
